/**
 * Copyright 2024 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */

import { getAxiosForClient } from '@/lib/axios';
import {
  keepPreviousData,
  type QueryClient,
  type QueryFunctionContext,
  useQuery,
} from '@tanstack/react-query';
import type { FormattedContentStackHeader } from '@/features/contentstack/interface';
import { getHeaderContent } from '@/features/contentstack/lib/header/getHeaderContent'; // eslint-disable-line no-restricted-imports
import { type PageContextType } from '@/next-types';
import { useSegments } from './getSegments';
import { useLocale } from '@/hooks/useLocale';
import { countryCodes } from '@/constants/locale';

const URL = '/api/contentstack/header';

const getCMSHeaderContent = async ({
  queryKey,
}: QueryFunctionContext<ReturnType<typeof getHeaderContentKey>>) => {
  const [, , { segments }] = queryKey;
  const { data } = await getAxiosForClient().get<FormattedContentStackHeader>(URL, {
    params: {
      segments: segments.join(','),
    },
  });

  return selector(data);
};

const selector = (data: FormattedContentStackHeader): FormattedContentStackHeader => data;

const getHeaderContentKey = (segments: string[]) => ['cms', 'header', { segments }] as const;

export const useCMSHeaderContent = () => {
  const { data: segments, isSuccess } = useSegments();
  const locale = useLocale();
  const enabled = isSuccess || locale === countryCodes.ptBr;
  const data = useGetCMSHeaderContent({
    enabled,
    segments: segments ?? [],
    staleTime: Infinity,
  });

  return {
    enabled,
    ...data,
  };
};

const useGetCMSHeaderContent = (options: {
  segments: string[];
  enabled?: boolean;
  staleTime?: number;
}) => {
  return useQuery({
    queryKey: getHeaderContentKey(options.segments.sort()),
    queryFn: getCMSHeaderContent,
    enabled: options.enabled,
    staleTime: Infinity,
    placeholderData: keepPreviousData,
  });
};

export const prefetchCMSHeaderContent = (
  queryClient: QueryClient,
  context: PageContextType,
  userSegments: string[],
) => {
  return queryClient.prefetchQuery({
    queryKey: getHeaderContentKey(userSegments),
    queryFn: () => getHeaderContent(context.req, context.query, userSegments),
  });
};
